/**
 * BulkActionResultModal
 *
 * Shown after a bulk accept/reject/request-info call when the response has at
 * least one failed entry. Lets the user see which jobs succeeded and which
 * failed (with the reason). All-success responses should surface as a toast
 * instead and never open this modal — the parent component is responsible for
 * that routing decision.
 *
 * A lookup of jobs is optional: passing it renders friendly names/statuses
 * alongside the ID. Without it the modal falls back to "Work order #123".
 */

import { Button } from '@/shared/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/shared/ui/dialog';
import type { WorkOrderActionResult } from '@skewb-works/contracts';
import { AlertTriangle, CheckCircle2, ChevronDown, ChevronRight, XCircle } from 'lucide-react';
import { useMemo, useState } from 'react';

export interface ResultModalJobLookup {
  id: number;
  name: string | null;
}

interface BulkActionResultModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Verb used in the heading: "3 of 5 rejected", "accepted", "info requests sent". */
  actionLabel: string;
  results: WorkOrderActionResult[];
  jobsLookup?: ResultModalJobLookup[];
}

export function BulkActionResultModal({
  open,
  onOpenChange,
  actionLabel,
  results,
  jobsLookup,
}: BulkActionResultModalProps) {
  const { succeeded, failed } = useMemo(() => {
    const succeeded: WorkOrderActionResult[] = [];
    const failed: WorkOrderActionResult[] = [];
    for (const r of results) {
      (r.success ? succeeded : failed).push(r);
    }
    return { succeeded, failed };
  }, [results]);

  // Succeeded list collapsed by default — the interesting data is the failures.
  const [showSucceeded, setShowSucceeded] = useState(false);

  const nameFor = (id: number): string => {
    const match = jobsLookup?.find((j) => j.id === id);
    return match?.name ?? `Job #${id}`;
  };

  const allFailed = succeeded.length === 0 && failed.length > 0;
  const mixed = succeeded.length > 0 && failed.length > 0;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <div className="flex items-start gap-3">
            <div
              className={`w-10 h-10 rounded-lg flex items-center justify-center shrink-0 ${
                allFailed ? 'bg-red-50 dark:bg-red-950' : 'bg-amber-50 dark:bg-amber-950'
              }`}
            >
              <AlertTriangle
                className={`w-5 h-5 ${allFailed ? 'text-red-600' : 'text-amber-600'}`}
              />
            </div>
            <div className="flex-1 min-w-0">
              <DialogTitle>
                {allFailed
                  ? `Couldn't ${actionLabel.toLowerCase()} ${failed.length} job${failed.length === 1 ? '' : 's'}`
                  : `${succeeded.length} of ${results.length} ${actionLabel.toLowerCase()}`}
              </DialogTitle>
              <DialogDescription className="mt-1">
                {mixed
                  ? 'Some updates went through — see the list below for the ones that failed.'
                  : 'None of the selected jobs could be updated.'}
              </DialogDescription>
            </div>
          </div>
        </DialogHeader>

        <div className="space-y-4 max-h-[50vh] overflow-y-auto pr-1">
          {/* Failed list — always expanded so failures are immediately visible */}
          {failed.length > 0 && (
            <section>
              <header className="flex items-center gap-2 mb-2 text-sm font-medium text-red-700 dark:text-red-400">
                <XCircle className="w-4 h-4" />
                Failed ({failed.length})
              </header>
              <ul className="space-y-2">
                {failed.map((r) => (
                  <li
                    key={r.id}
                    className="rounded-md border border-red-200 dark:border-red-900/50 bg-red-50/50 dark:bg-red-950/20 p-3"
                  >
                    <div className="text-sm font-medium text-foreground">{nameFor(r.id)}</div>
                    <div className="text-xs text-red-700 dark:text-red-400 mt-1">
                      {r.message ?? 'No reason provided'}
                    </div>
                  </li>
                ))}
              </ul>
            </section>
          )}

          {/* Succeeded list — collapsed by default */}
          {succeeded.length > 0 && (
            <section>
              <button
                type="button"
                onClick={() => setShowSucceeded((v) => !v)}
                className="flex items-center gap-2 text-sm font-medium text-green-700 dark:text-green-400 hover:underline"
                aria-expanded={showSucceeded}
              >
                {showSucceeded ? (
                  <ChevronDown className="w-4 h-4" />
                ) : (
                  <ChevronRight className="w-4 h-4" />
                )}
                <CheckCircle2 className="w-4 h-4" />
                Succeeded ({succeeded.length})
              </button>
              {showSucceeded && (
                <ul className="mt-2 space-y-1 pl-6 text-xs text-muted-foreground">
                  {succeeded.map((r) => (
                    <li key={r.id}>{nameFor(r.id)}</li>
                  ))}
                </ul>
              )}
            </section>
          )}
        </div>

        <DialogFooter>
          <Button onClick={() => onOpenChange(false)}>Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
